import { Card } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, ComposedChart, Scatter } from "recharts";
import { TrendAnalysis } from "@/hooks/use-trend-analysis";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

interface TrendChartProps {
  data: TrendAnalysis;
  metric: string;
  unit: string;
  color?: string;
}

export function TrendChart({ data, metric, unit, color = "#3b82f6" }: TrendChartProps) {
  // Função para formatar moeda brasileira
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (!data || data.historical.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground text-center">Sem dados históricos disponíveis</p>
      </Card>
    );
  }

  // Combinar dados históricos, linha de tendência e projeção
  const chartData = data.historical.map((h, i) => ({
    period: h.period,
    value: h.value,
    trendValue: data.trendLine[i]?.value,
    anomaly: h.anomaly,
    isHistorical: true
  }));

  // Adicionar projeções
  data.projection.forEach(p => {
    chartData.push({
      period: p.period,
      value: undefined as any,
      trendValue: p.value,
      anomaly: false,
      isHistorical: false
    });
  });

  const getTrendIcon = () => {
    switch (data.summary.trend) {
      case 'growing': return <TrendingUp className="h-4 w-4" />;
      case 'declining': return <TrendingDown className="h-4 w-4" />;
      default: return <Minus className="h-4 w-4" />;
    }
  };

  const getTrendColor = () => {
    switch (data.summary.trend) {
      case 'growing': return metric === 'cost' || metric === 'alerts' ? 'destructive' : 'default';
      case 'declining': return metric === 'cost' || metric === 'alerts' ? 'default' : 'destructive';
      default: return 'secondary';
    }
  };

  return (
    <Card className="p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-semibold">Análise de Tendência - {metric}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Últimos {data.historical.length} meses + projeção de 3 meses
          </p>
        </div>
        <div className="text-right">
          <Badge variant={getTrendColor()} className="flex items-center gap-1">
            {getTrendIcon()}
            <span className="capitalize">{data.summary.trend === 'growing' ? 'Crescimento' : data.summary.trend === 'declining' ? 'Declínio' : 'Estável'}</span>
          </Badge>
          <p className="text-sm text-muted-foreground mt-2">
            Mudança média: {data.summary.averageChange.toFixed(1)}%/mês
          </p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={color} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="period" 
            className="text-xs"
            tick={{ fill: 'hsl(var(--muted-foreground))' }}
          />
          <YAxis 
            className="text-xs"
            tick={{ fill: 'hsl(var(--muted-foreground))' }}
            label={{ value: unit, angle: -90, position: 'insideLeft' }}
            tickFormatter={(value) => unit === 'R$' ? formatCurrency(value) : value}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '8px'
            }}
            formatter={(value: any) => {
              if (typeof value === 'number') {
                // Se o unit for R$, formatar como moeda brasileira
                if (unit === 'R$') {
                  return formatCurrency(value);
                }
                return value.toFixed(2) + ' ' + unit;
              }
              return value;
            }}
          />
          <Legend />
          
          {/* Área sombreada para dados históricos */}
          <Area
            type="monotone"
            dataKey="value"
            fill="url(#colorValue)"
            stroke="none"
            name="Valor Real"
          />
          
          {/* Linha de valores reais */}
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={{ r: 4, fill: color }}
            name="Valor Real"
            connectNulls={false}
          />
          
          {/* Linha de tendência */}
          <Line
            type="monotone"
            dataKey="trendValue"
            stroke="#10b981"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            name="Tendência"
          />
          
          {/* Marcadores de anomalias */}
          <Scatter
            dataKey="value"
            fill="#ef4444"
            shape={(props: any) => {
              if (props.payload.anomaly && props.payload.value) {
                return (
                  <circle
                    cx={props.cx}
                    cy={props.cy}
                    r={8}
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth={2}
                  />
                );
              }
              return null;
            }}
            name="Anomalia"
          />
        </ComposedChart>
      </ResponsiveContainer>

      <div className="mt-4 p-4 bg-muted/50 rounded-lg">
        <p className="text-sm font-medium">Previsão para o próximo mês:</p>
        <p className="text-2xl font-bold text-primary mt-1">
          {unit === 'R$' ? formatCurrency(data.summary.forecast) : `${data.summary.forecast.toFixed(2)} ${unit}`}
        </p>
      </div>
    </Card>
  );
}
